// View model for the result card: one shape for every outcome of a comp request. Verdict and the
// one number that matters on top, three stats, the check dots, then one action row (main button
// bottom-right). The kind picks the middle strip and the actions.
import { centsText, decimalCents, formatAmount, formatDateTimeUTC, storedAmount, trimDecimal, usdValue } from './format';
import { bonusBackofficeUrl, formatShortUTC } from './timeline';
import { checkChips, formatMoney, notEligibleView, TOTAL_CHECKS } from './notEligible';
import { needsExceptionView } from './exception';
import { eligibleView } from './eligible';
import { canOverride, issueState } from './issue';

const MINUTE_MS = 60 * 1000;

// formatCountdown: "2d 15h", "5h 20m"; "closed" once past.
export const formatCountdown = (from, to) => {
  if (!from || !to) return '—';
  const minutes = Math.floor((new Date(to) - new Date(from)) / MINUTE_MS);
  if (minutes <= 0) return 'closed';
  const d = Math.floor(minutes / (24 * 60));
  const h = Math.floor((minutes % (24 * 60)) / 60);
  return d > 0 ? `${d}d ${h}h` : `${h}h ${minutes % 60}m`;
};

const tagWageringSet = (p) => p && p.wagering !== null && p.wagering !== undefined;

// kindOf: which card to show. A saved comp (queued / pushed / issued) wins over the live decision.
export const kindOf = (check, exception = {}) => {
  if (check.issued?.status === 'issued') return 'issued';
  if (check.issued?.status === 'pushed') return 'pushed';
  if (check.issued?.status === 'pending') return 'queued';
  switch (check.decision) {
    case 'eligible': return 'eligible';
    case 'manual_check': return check.proposal ? 'manual' : 'unknown';
    case 'needs_exception': return exception.granted && check.proposal ? 'granted' : 'exception';
    case 'not_eligible': return notEligibleView(check) ? 'not_eligible' : 'unknown';
    default: return 'unknown';
  }
};

const TONES = {
  eligible: 'positive', granted: 'positive', queued: 'positive', pushed: 'positive', issued: 'positive',
  manual: 'warning', exception: 'warning', not_eligible: 'negative', unknown: 'negative',
};

const ICONS = {
  eligible: 'check', granted: 'check', manual: 'warning', exception: 'warning', queued: 'clock', pushed: 'send', issued: 'check', unknown: 'help',
};

const depositCount = (d) => (d?.by_currency || []).reduce((n, c) => n + (c.count || 0), 0);

function balanceStat(b) {
  const limit = formatMoney(centsText(b.limit_usd_cents || 0), 'USD');
  if (b.status === 'skipped' || b.status === undefined) return { label: 'Balance', value: 'not checked', sub: 'check 3 skipped', muted: true };
  if (b.status === 'unknown') {
    return { label: 'Balance', value: 'unknown', sub: b.missing_rates?.length ? `no rate for ${b.missing_rates.join(', ')}` : 'cannot verify', tone: 'negative' };
  }
  const total = formatMoney(centsText(b.total_usd_cents), 'USD');
  if (b.status === 'fail') return { label: 'Balance', value: total, sub: `must be below ${limit}`, tone: 'negative' };
  return { label: 'Balance', value: total, sub: `below the ${limit} limit` };
}

function stats(check, kind, now) {
  const d = check.deposits || {};
  const period = check.period?.period;
  const count = depositCount(d);
  const top = (d.by_currency || [])[0];
  const native = top && top.currency !== d.basis_currency ? ` · ${formatAmount(top.amount, top.currency)}` : '';
  const depositSub = kind === 'exception'
    ? `band from ${formatMoney(d.exception_min, d.basis_currency)} · full from ${formatMoney(d.eligible_min, d.basis_currency)}`
    : `${count} deposit${count === 1 ? '' : 's'}${native}`;
  return [
    { label: 'Deposited this period', value: d.measured ? formatMoney(d.measured, d.basis_currency) : '—', sub: d.measured ? depositSub : 'no deposits measured' },
    period
      ? { label: 'Period closes in', value: formatCountdown(now, period.period_end), sub: `${formatShortUTC(period.period_start)} → ${formatShortUTC(period.period_end)}` }
      : { label: 'Period closes in', value: '—', sub: 'no active period', muted: true },
    balanceStat(check.balance || {}),
  ];
}

// chips: the checks as dots; a failed or unverifiable check is red.
const chips = (check) => checkChips(check).map((c) => ({ ...c, status: c.status || 'skipped' }));

// steps: the checks as tiles with the number behind each (detailed view).
function steps(check, headline) {
  const d = check.deposits || {};
  const b = check.balance || {};
  const period = check.period?.period;
  let balanceSub = 'not run';
  if (b.status === 'unknown') balanceSub = 'cannot verify';
  else if (b.status && b.status !== 'skipped') balanceSub = `${formatMoney(centsText(b.total_usd_cents), 'USD')} on all accounts`;
  const vipSub = { pass: 'VIP', fail: 'not VIP' }[check.vip?.status] || 'not run';
  let compSub = 'not run';
  if (check.proposal) compSub = headline?.label?.startsWith('Bonus amount') ? headline.value : 'proposed';
  const subs = [
    period ? `Started ${formatShortUTC(period.period_start)}` : 'No active period',
    d.measured ? `${formatMoney(d.measured, d.basis_currency)} · min ${formatMoney(d.eligible_min, d.basis_currency)}` : 'not run',
    balanceSub,
    vipSub,
    compSub,
  ];
  return chips(check).map((c, i) => ({ ...c, label: c.label.replace(' · not run', ''), index: i + 1, sub: subs[i] }));
}

const stoppedAt = (check) => {
  const hit = [['period', 1], ['deposits', 2], ['balance', 3], ['vip', 4]].find(([k]) => ['fail', 'unknown'].includes(check[k]?.status));
  return hit ? hit[1] : null;
};

// inputsNeeded: what the manager must still fill before issuing (manual wagering, amount).
const inputsNeeded = (check, overrides) => ({
  wagering: !tagWageringSet(check.proposal) && overrides.wagering == null,
  amount: !check.proposal.issue_amount && !overrides.amount,
});

// rateLine: "1 BTC = 81,845.10 USD" — what one issue unit was worth when converted (2 decimals,
// more for coins worth under 1); null without a rate.
export const rateLine = (rate, fiat, unit) => {
  const n = Number(rate);
  if (!rate || !(n > 0)) return null;
  const text = n >= 1 ? n.toFixed(2) : trimDecimal(n.toFixed(6));
  return `1 ${unit} = ${formatAmount(text)} ${fiat}`;
};

// amountHeadline: the comp amount to issue (adjusted, proposed, or still to convert).
function amountHeadline(check, overrides, tone) {
  const p = check.proposal;
  const wagering = tagWageringSet(p) ? p.wagering : overrides.wagering;
  const wageringText = wagering != null ? `${wagering}x wagering` : 'wagering not set';
  if (overrides.amount) {
    const currency = p.issue_amount ? p.issue_currency : p.basis_currency;
    return { label: 'Bonus amount · adjusted', value: formatAmount(overrides.amount, currency), sub: wageringText, tone };
  }
  if (!p.issue_amount) {
    return { label: 'Bonus amount', value: `${formatMoney(p.basis_amount, p.basis_currency)} → ${p.issue_currency}`, sub: 'convert manually', tone: 'warning' };
  }
  const converted = p.basis_currency !== p.issue_currency;
  const value = usdValue(p.issue_amount, p.issue_rate_usd);
  const usd = converted ? `≈ ${value ? formatMoney(value, 'USD') : formatMoney(p.basis_amount, p.basis_currency)} · ` : '';
  // this card does not list the check's reasons, so the missing rule is named here
  const rounded = p.issue_round_to
    ? `rounded up to ${p.issue_round_to} ${p.issue_currency} · `
    : `not rounded: no rule for ${p.issue_currency}, contact Ziga · `;
  const rate = converted ? rateLine(p.issue_rate_usd, p.basis_currency, p.issue_currency) : null;
  return {
    label: 'Bonus amount', value: formatAmount(storedAmount(p.issue_amount), p.issue_currency), sub: `${usd}${rounded}${wageringText}`,
    rate: rate && `${rate} · today's rate`, tone,
  };
}

function eligibleHead(check, kind, overrides) {
  const p = check.proposal;
  const period = check.period?.period || {};
  if (kind === 'manual') {
    return {
      eyebrow: `Manual check · ${TOTAL_CHECKS} of ${TOTAL_CHECKS} checks ran`,
      title: 'Pick the wagering to issue',
      text: 'The player\'s tags map to different wagering requirements.',
    };
  }
  const need = inputsNeeded(check, overrides);
  const missing = [need.wagering, need.amount].filter(Boolean).length;
  let eyebrow = `Eligible · ${TOTAL_CHECKS} of ${TOTAL_CHECKS} checks passed`;
  if (missing) eyebrow = `Eligible · ${missing} input${missing > 1 ? 's' : ''} needed`;
  if (kind === 'granted') eyebrow = 'Eligible with exception';
  let text = `${p.rate_percent}% of deposits since ${formatShortUTC(period.period_start)} UTC. Valid ${p.valid_days} days once issued.`;
  if (need.wagering && need.amount) text = `No wagering tag and no ${p.issue_currency} rate today. Fill both to issue.`;
  else if (need.wagering) text = 'No wagering tag decides this comp. Set the wagering to issue.';
  else if (need.amount) text = `No ${p.issue_currency} rate today. Enter the amount to issue.`;
  return { eyebrow, title: `${p.name} ready to issue`, text };
}

function savedLine(issued) {
  const added = `Added by ${issued.created_by} on ${formatDateTimeUTC(issued.created_at)}`;
  if (issued.status === 'issued') return `${added} · issued as bonus #${issued.issued_bonus_id}`;
  if (issued.status === 'pushed') return `${added} · pushed ${formatDateTimeUTC(issued.pushed_at)}`;
  return issued.updated_by ? `${added} · corrected by ${issued.updated_by} on ${formatDateTimeUTC(issued.updated_at)}` : added;
}

// savedSub: the fiat the comp is worth, then the wagering.
function savedSub(i) {
  const parts = [];
  const value = usdValue(i.amount, i.exchange_rate);
  if (value) {
    parts.push(`≈ ${formatMoney(value, 'USD')}`);
  } else if (i.eligible_amount && i.eligible_currency !== i.currency) {
    parts.push(`≈ ${formatMoney(i.eligible_amount, i.eligible_currency)}`);
  }
  parts.push(`${i.wagering}x wagering`);
  return parts.join(' · ');
}

// savedRate: the rate the saved comp was converted at.
const savedRate = (i) => {
  const rate = i.eligible_currency !== i.currency ? rateLine(i.exchange_rate, i.eligible_currency, i.currency) : null;
  return rate && `${rate} · rate used`;
};

// SAVED_HEADS: the head of a saved comp per kind (an override says so in the eyebrow).
const SAVED_HEADS = {
  queued: { eyebrow: 'Eligible · saved', override: 'Override · saved · checks failed', title: 'Queued for issuing', pill: 'Queued', tone: 'ink' },
  pushed: { eyebrow: 'Done · nothing to do', override: 'Override · pushed', title: 'Pushed for issuing', pill: 'Pushed', tone: 'positive' },
  issued: { eyebrow: 'Done · issued', override: 'Override · issued', title: 'Bonus issued', pill: 'Issued', tone: 'positive' },
};

function savedHead(check, kind) {
  const i = check.issued;
  const h = SAVED_HEADS[kind];
  return {
    eyebrow: isOverride(check) ? h.override : h.eyebrow,
    title: h.title,
    text: savedLine(i),
    headline: { label: '', pill: h.pill, value: formatAmount(storedAmount(i.amount), i.currency), sub: savedSub(i), rate: savedRate(i), tone: h.tone },
  };
}

function exceptionHead(check) {
  const v = needsExceptionView(check);
  const p = check.proposal;
  let comp = '';
  if (p) comp = p.issue_amount ? formatAmount(storedAmount(p.issue_amount), p.issue_currency) : formatMoney(p.basis_amount, p.basis_currency);
  return {
    eyebrow: v.eyebrow,
    title: v.title,
    text: 'You can grant a one-off exception. Tell the player it\'s a one-off.',
    reasons: v.reasons,
    headline: { label: 'Short by', value: v.metrics.find((m) => m.accent)?.value, sub: comp ? `comp if granted: ${comp}` : '', tone: 'warning' },
    guidance: v.guidance,
  };
}

function notEligibleHead(check) {
  const v = notEligibleView(check);
  const base = { eyebrow: `Not eligible${v.step > 1 ? ' yet' : ''} · check ${v.step} failed`, title: v.title, text: v.text };
  if (v.icon === 'deposits') {
    const d = check.deposits;
    return {
      ...base,
      headline: { label: 'Still missing', value: v.metrics[2].value, sub: `of ${formatMoney(d.eligible_min, d.basis_currency)}`, tone: 'negative' },
      progress: { pct: v.bar.pct, text: `${formatMoney(centsText(decimalCents(d.measured || '0')), d.basis_currency)} · ${Math.floor(v.bar.pct)}% of the requirement` },
    };
  }
  if (v.icon === 'balance') {
    const b = check.balance;
    return {
      ...base,
      headline: { label: 'Over the limit by', value: v.metrics[2].value, sub: `${formatMoney(centsText(b.total_usd_cents), 'USD')} now · limit ${formatMoney(centsText(b.limit_usd_cents), 'USD')}`, tone: 'negative' },
    };
  }
  if (v.icon === 'vip') return { ...base, eyebrow: `Not eligible · check ${v.step} failed` };
  return { ...base, text: 'Nothing to measure this week, so the other checks did not run.' };
}

function head(check, kind, overrides) {
  switch (kind) {
    case 'eligible':
    case 'granted':
    case 'manual':
      return { ...eligibleHead(check, kind, overrides), headline: amountHeadline(check, overrides, kind === 'manual' ? 'ink' : 'positive') };
    case 'queued':
    case 'pushed':
    case 'issued':
      return savedHead(check, kind);
    case 'exception':
      return exceptionHead(check);
    case 'not_eligible':
      return notEligibleHead(check);
    default: {
      const step = stoppedAt(check);
      return {
        eyebrow: `Cannot verify${step ? ` · check ${step}` : ''}`,
        title: 'Can\'t decide yet',
        text: '',
        reasons: check.reasons?.length ? check.reasons : ['The checks could not reach a decision.'],
      };
    }
  }
}

const ISSUABLE = ['eligible', 'granted', 'manual', 'queued'];
// FINAL: saved comps that can no longer change.
export const FINAL = ['pushed', 'issued'];

// isOverride: the saved comp was issued although a check failed (no proposal behind it).
const isOverride = (check) => Boolean(check.issued) && check.issued.status !== 'cancelled' && !check.proposal;

// cancelledNote: who cancelled the period's comp; the comp can then be issued again.
const cancelledNote = (i) => (i?.status === 'cancelled'
  ? `Cancelled by ${i.updated_by || i.created_by} on ${formatDateTimeUTC(i.updated_at)} (${formatAmount(storedAmount(i.amount), i.currency)}). Issuing again saves it anew.`
  : null);

// resultCardView describes the card for a check. opts: { exception, overrides, now }.
export const resultCardView = (check, { exception = {}, overrides = {}, now = check.checked_at } = {}) => {
  const kind = kindOf(check, exception);
  const h = head(check, kind, overrides);
  const override = isOverride(check);
  // issued from the proposal (the normal flow); an override row is edited through the override form
  const issuable = ISSUABLE.includes(kind) && !override;
  return {
    kind,
    override,
    canOverride: ['not_eligible', 'unknown'].includes(kind) && canOverride(check),
    cancelled: cancelledNote(check.issued),
    tone: override && kind === 'queued' ? 'warning' : TONES[kind],
    icon: kind === 'not_eligible' ? notEligibleView(check).icon : ICONS[kind],
    ...h,
    stats: stats(check, kind, now),
    chips: chips(check),
    steps: steps(check, h.headline),
    issue: (issuable || FINAL.includes(kind)) && check.proposal ? issueState(check, overrides) : null,
    // what Issue / Update submits (a queued comp's headline shows the saved amount instead)
    submitAmount: issuable && check.proposal ? amountHeadline(check, overrides, 'ink').value : null,
    inputs: issuable ? inputsNeeded(check, overrides) : { wagering: false, amount: false },
    wagering: check.proposal && (issuable || FINAL.includes(kind)) ? eligibleView(check, overrides).wagering : null,
    // the back office bonus, once the comp is issued
    bonusUrl: kind === 'issued' ? bonusBackofficeUrl(check.issued.issued_bonus_id) : null,
  };
};
